import {
    useCallback,
} from 'react'
import {
    FormHelperText,
} from '@mui/material'
import {
    MobileDatePicker,
} from '@mui/x-date-pickers/MobileDatePicker'

import {
    noop,
} from 'lodash'
import Label from 'app/shared-components/Label'
import {
    dateToDateStr,
    minDate,
    strToMoment,
} from 'app/utils/date'

import ToolbarWithKeyboardViewSwitch from '../DateTimeZonedSelect/components/ToolbarWithKeyboardViewSwitch'
import LayoutWithKeyboardView from '../DateTimeZonedSelect/components/LayoutWithKeyboardView'
import InputSingleline from '../InputSingleline'
import useStyles from './DateSelect.styles'
import useDateInputError from './hooks/useDateInputError'

type Props = {
    title?: string,
    name?: string,
    hasError?: string,
    onChange: (value: string, event: any) => void,
    disabled?: boolean,
    required?: boolean,
    value?: string,
    className?: string,
    disablePast?: boolean,
    setErrors?: (error: string[]) => void,
}

const DateSelect = ({
    title = '',
    name = '',
    hasError = '',
    onChange,
    value = null,
    disabled = false,
    required = false,
    className = '',
    disablePast = false,
    setErrors = noop,
}: Readonly<Props>) => {
    const {
        classes, cx,
    } = useStyles()

    const {
        onError,
        isError,
    } = useDateInputError({
        name,
        setFormError: setErrors,
    })

    const handleOnChange = useCallback((date) => {
        const newValue = dateToDateStr(date)

        onChange(newValue, {
            target: {
                name,
                value: newValue,
            },
        })
    }, [
        name,
        onChange,
    ])

    if (disabled) {
        return (
            <InputSingleline
                name={name}
                title={title}
                value={value}
                disabled
            />
        )
    }

    return (
        <div
            className={className}
            data-testid={`dateSelect-${name}`}
        >
            <Label
                title={title}
                hasError={hasError}
            />
            <div
                className={cx(
                    classes.dateField,
                    {
                        [classes.isRequired]: required,
                        [classes.inputError]: isError,
                    },
                )}
            >
                {required && (
                    <div
                        className={cx({
                            [classes.requiredMark]: true,
                            [classes.requiredMarkError]: isError,
                        })}
                        data-testid="dateSelect-required-mark"
                    />
                )}
                <MobileDatePicker
                    className={classes.input}
                    value={strToMoment(value)}
                    format="DD.MM.YYYY"
                    onChange={handleOnChange}
                    slotProps={{
                        textField: {
                            variant: 'standard',
                            fullWidth: true,
                        },
                    }}
                    slots={{
                        layout: LayoutWithKeyboardView,
                        toolbar: ToolbarWithKeyboardViewSwitch,
                    }}
                    disablePast={disablePast}
                    disableOpenPicker
                    minDate={strToMoment(minDate)}
                    onError={onError}
                />
                <FormHelperText
                    error={Boolean(hasError)}
                    className={classes.errorMessage}
                >
                    {hasError}
                </FormHelperText>
            </div>
        </div>
    )
}

export default DateSelect
